import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
} from "@mui/material";
import {
  SmartAutoComplete,
  SmartInputBaseProps,
} from "@nodeon/react-components";
import React from "react";
import { FieldValues } from "react-hook-form";
import { useAISRouters } from "~/pages/ais-vessel-page/hooks/useAISRouters";

type Props<T extends FieldValues> = Omit<
  Parameters<typeof SmartAutoComplete>[0],
  "options" | "getOptionLabel"
> &
  SmartInputBaseProps<T>;

export type AISRouterAutocompleteOption = {
  deviceId: string;
};

export const AISRouterSmartAutocomplete = <
  T extends FieldValues,
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = undefined,
>({
  multiple = false,
  disabled,
  disableClearable,
  freeSolo,
  name,
  label,
  ...props
}: Props<T>): JSX.Element => {
  const { listQuery, getRouterById } = useAISRouters();

  const getOptionLabel = React.useCallback(
    (
      opt:
        | AISRouterAutocompleteOption
        | AutocompleteFreeSoloValueMapping<TFreeSolo>,
    ): string => {
      if (typeof opt === "string") return opt;
      const item = getRouterById(opt.deviceId);
      if (!item) return opt.deviceId.toString();
      return `${item.deviceId} - ${item.name} - ${item.ipAddress ?? ""}`;
    },
    [getRouterById],
  );

  const getOptionDisabled = React.useCallback(() => {
    return false;
  }, []);

  const options = React.useMemo(
    () => [
      ...(listQuery.data?.map(
        (x): AISRouterAutocompleteOption => ({
          deviceId: x.deviceId,
        }),
      ) ?? []),
    ],
    [listQuery],
  );

  const isOptionEqualToValue = React.useCallback(
    (opt: AISRouterAutocompleteOption, val: AISRouterAutocompleteOption) => {
      return opt.deviceId === val.deviceId;
    },
    [],
  );

  return (
    <SmartAutoComplete<
      T,
      AISRouterAutocompleteOption,
      TMultiple,
      TDisableClearable,
      TFreeSolo
    >
      {...(props as Omit<
        AutocompleteProps<
          AISRouterAutocompleteOption,
          TMultiple,
          TDisableClearable,
          TFreeSolo
        >,
        "options"
      >)}
      name={name}
      label={label}
      multiple={multiple as TMultiple}
      disableClearable={disableClearable as TDisableClearable}
      freeSolo={freeSolo as TFreeSolo}
      options={options}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
};
